import { getLocale } from '@borealis/aurora';
import { getAt } from '@finn-no/mobility-item-insertion-shared';
import { Trans } from '@lingui/react/macro';
import { Bold } from '@tiptap/extension-bold';
import { BulletList } from '@tiptap/extension-bullet-list';
import { Document } from '@tiptap/extension-document';
import { ListItem } from '@tiptap/extension-list-item';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Text } from '@tiptap/extension-text';
import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import '@warp-ds/components/icon';
import { Button } from '@warp-ds/react';
import { useEffect, useRef, useState } from 'react';
import { type FieldError, useController, useFormContext } from 'react-hook-form';
import type { DescriptionVariant } from '../../../types/ai-description.js';
import type { HtmlWidgetModel } from '../../../types/widget.js';
import { useAIDescriptionSuggestions } from '../../hooks/useAIDescriptionSuggestions.js';
import { getHuckConfig } from '../../utils/getHuckConfig.js';
import { AiDescriptionButton } from '../ai-description/AiDescriptionButton.js';
import { AiDescriptionFeedback } from '../ai-description/AiDescriptionFeedback.js';
import { AiDescriptionModal } from '../ai-description/AiDescriptionModal.js';

const CustomBold = Bold.extend({
  renderHTML({ HTMLAttributes }) {
    return ['b', HTMLAttributes, 0]; // Render "bold" marks as "b" instead of "strong"
  },
});

type TipTapProps = {
  name: string;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  mandatory?: boolean;
  invalid: boolean;
  helpText?: string;
};

const HTMLWidget = ({ value, onChange, label, mandatory, name, invalid, helpText }: TipTapProps) => {
  const { clearErrors } = useFormContext();
  const isInternalUpdate = useRef(false);

  // The useEditor hook should be optimized for re-renders - it can take in a deps array as a second argument
  const editor = useEditor({
    extensions: [Document, Paragraph, Text, CustomBold, BulletList, ListItem],
    content: value,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: `overflow-y-scroll block text-m mb-0 leading-m s-text s-bg ${!invalid ? 's-border' : 's-border-negative'} active:s-border-selected rounded-bl-4 rounded-br-4 border-t-0 border-l-1 border-r-1 border-b-1 py-12 px-8 block w-full focusable focus:[--w-outline-offset:-2px] caret-current h-[200]`,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (html !== value) {
        isInternalUpdate.current = true;
        onChange(
          html
            .replaceAll(/<li><p>(.*?)<\/p><(\/?)(ol|li|ul)>/gi, '<li>$1<$2$3>') // TipTap/PM wraps all text sections within bullet lists in paragraphs, which can lead to unintended results - this removes the paragraph
            .replaceAll(/<p><\/p>/gi, '<p><br></p>') // TipTap/PM will show empty paragraphs, however rendering them in HTML will collapse them - so we need to make sure they include a breakline to render correctly on the item page
            .replace(/(<p><br\s*\/?><\/p>\s*)+$/gi, ''), // Strip trailing empty paragraphs to prevent bloated HTML
        );
      }
    },
    onBlur: () => {
      clearErrors(name);
    },
  });

  // Sync editor content only when value changes externally (e.g., from AI suggestion)
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  return (
    <>
      <Toolbar editor={editor} label={label} mandatory={mandatory} invalid={invalid} />
      <EditorContent
        itemID="item-insertion-editor"
        id="item-insertion-editor"
        editor={editor}
        scrolling="smooth"
        data-testid={name}
        data-attribute={name}
      />
      {!!helpText && <span className="text-xs mt-4 block s-text-negative">{helpText}</span>}
    </>
  );
};

const Toolbar = ({
  editor,
  label,
  mandatory,
  invalid,
}: {
  editor: Editor | null;
  label?: string;
  mandatory?: boolean;
  invalid: boolean;
}) => {
  return (
    <>
      <label
        htmlFor="item-insertion-editor"
        className="antialiased block relative text-s font-bold pb-4 cursor-pointer s-text"
      >
        {label}
        {!mandatory && (
          <span className="pl-8 font-normal text-s s-text-subtle">
            (
            <Trans
              id="mobility-item-insertion.form.description.label.optional"
              comment="lowercase optional label for the rich text editor/description in the form"
            >
              optional
            </Trans>
            )
          </span>
        )}
      </label>
      <div className={`flex items-center gap-8 border rounded-tl-4 rounded-tr-4 p-8 ${invalid && 's-border-negative'}`}>
        <Button
          className="s-text hover:s-text-link !p-0 !hover:bg-transparent"
          utility
          quiet
          onClick={() => editor?.chain().focus().toggleBold().run()}
          disabled={!editor?.can().chain().focus().toggleBold().run()}
        >
          <strong className="block w-24 h-24" style={{ lineHeight: '26px' }}>
            B
          </strong>
        </Button>
        <Button
          className="s-text hover:s-text-link !p-0 !hover:bg-transparent"
          utility
          quiet
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          <w-icon name="ListSort" size="small" locale={getLocale()} />
        </Button>
      </div>
    </>
  );
};

type RichTextWidgetProps = Omit<HtmlWidgetModel, 'type'>;

export const ControlledHTMLWidget = ({ name, label, mandatory, rules }: RichTextWidgetProps) => {
  const {
    field: { onChange, value },
    formState: { errors },
  } = useController({ name, rules });

  const [fetchEnabled, setFetchEnabled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [selectedSuggestionId, setSelectedSuggestionId] = useState<string | null>(null);
  const [feedbackKey, setFeedbackKey] = useState(0);
  const config = getHuckConfig();
  const isDescriptionField = name === 'description';
  const isAiDescriptionEnabled = config.toggles?.mobilityItemInsertionAiDescriptionSuggestions ?? false;

  const {
    data: suggestions,
    isFetching,
    error: apiError,
    refetch,
  } = useAIDescriptionSuggestions(config.itemId, isAiDescriptionEnabled);

  // Auto-open modal when suggestions are successfully loaded after user request
  useEffect(() => {
    if (fetchEnabled && !isFetching && suggestions && suggestions.descriptions.length > 0) {
      setIsModalOpen(true);
      setFetchEnabled(false); // Reset so button can be clicked again
      setFeedbackKey((prev) => prev + 1); // Reset feedback component
    }
  }, [fetchEnabled, isFetching, suggestions]);

  let helpText: string | undefined;

  const error = getAt<FieldError>(errors, name);
  if (error) {
    helpText = (error.message as string) ?? 'Validation error';
  }

  const handleFetchSuggestions = () => {
    setFetchEnabled(true);
    refetch(); // Always fetch fresh suggestions, even if cached data exists
  };

  const handleSelectSuggestion = async (suggestion: DescriptionVariant) => {
    // Record usage tracking
    try {
      await fetch(`${config.root}/api/description/${config.itemId}/used`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': config.csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          id: suggestion.id,
          variant_id: suggestion.variantId,
        }),
      });
    } catch (error) {
      // Don't block the user if tracking fails, just log it
      console.error('[AI Description] Failed to record usage:', error);
    }

    // Convert plain text to HTML paragraph
    const htmlContent = `<p>${suggestion.text}</p>`;
    onChange(htmlContent);
    setIsModalOpen(false);
    setShowFeedback(true);
    setSelectedSuggestionId(suggestion.id);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFetchEnabled(false); // Reset when closing without selecting
    setShowFeedback(true);
  };

  // Check if there's meaningful content in the description field
  const hasExistingContent = value && value !== '<p><br></p>' && value !== '<p></p>' && value.trim() !== '';

  return (
    <>
      {isDescriptionField && isAiDescriptionEnabled && (
        <>
          <AiDescriptionButton onClick={handleFetchSuggestions} disabled={isFetching} isLoading={isFetching} />
          {fetchEnabled && apiError && (
            <div className="mt-16 p-16 s-bg-negative-subtle rounded-8">
              <p className="s-text-negative">Error: {(apiError as Error).message}</p>
            </div>
          )}
          {fetchEnabled && suggestions && suggestions.descriptions.length === 0 && (
            <div className="mt-16 p-16 s-bg-info-subtle rounded-8">
              <p className="s-text">No suggestions available for this listing.</p>
            </div>
          )}
          <AiDescriptionModal
            isOpen={isModalOpen}
            suggestions={suggestions?.descriptions ?? []}
            onClose={handleCloseModal}
            onSelect={handleSelectSuggestion}
            hasExistingContent={hasExistingContent}
          />
        </>
      )}
      <HTMLWidget
        value={value}
        onChange={onChange}
        label={label}
        mandatory={mandatory}
        name={name}
        invalid={!!error}
        helpText={helpText}
      />
      {isDescriptionField && showFeedback && !isModalOpen && (
        <AiDescriptionFeedback key={feedbackKey} suggestionId={selectedSuggestionId} />
      )}
    </>
  );
};
